import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { AxiosError } from 'axios';
import Loading from 'common/ui/Loading';
import { useQueryString } from 'common/useQueryString';
import externalBidsService from './externalBidsService';
import loggingService from 'common/logging/loggingService';
import ErrorList from 'common/ui/ErrorList';
import useInterval from 'common/ui/useInterval';
import BidRow from './bidRow';
import { BidColumn, bidColumns, defaultColumns } from './bidColumns';
import BidsComponentParameters from './bidComponentParameters';
import PhysicalLocation, { AllRegionsHomeLocation, LocationBase } from 'domain/physicalLocation';
import { Form } from 'react-bootstrap';
import Commodity from 'domain/commodity';

import './BidsComponent.scss';
import CmeLogo from './cme_logo.png';
import { CSVLink } from 'react-csv';
import { Headers } from 'react-csv/components/CommonPropTypes';
import { formatDate, formatUTCDate } from 'common/formatters/dates';
import LastUpdated from 'common/ui/LastUpdated';
import moment from 'moment';
import UnitPicker from 'features/settings/commodities/UnitPicker';
import { Unit } from 'domain/unit';

const BidsContext = React.createContext<BidsComponentParameters>({
    apiKey: null,
    columns: null,
    commodities: null,
    locations: null,
    byCommodity: false,
    hideRowsWithoutBasis: true,
    preview: false
});

function excludeEmptyString(val: string): string {
    return val?.length === 0 ? null : val;
}

export const BidsComponentPage: React.FC<any> = () => {
    
    const query = useQueryString();

    const bidsContext: BidsComponentParameters = {
        apiKey: query.get('key'),
        columns: excludeEmptyString(query.get('cols'))?.split(','),
        commodities: excludeEmptyString(query.get('comms'))?.split(',').map(m => Number.parseInt(m)),
        locations: excludeEmptyString(query.get('locs'))?.split(','),
        byCommodity: query.get('byComm') === "true",
        hideRowsWithoutBasis: query.get('hideRows') === "true",
        preview: query.get('preview') === "true"
    };

    return <BidsContext.Provider value={bidsContext}>
        <BidsComponent />
    </BidsContext.Provider>
}

type BidRowAccessor = ((row: BidRow) => any);

interface BidsComponentProps {
}

const BID_REFRESH_INTERVAL = 1000 * 60; //One minute
const REFRESH_EXPIRATION_TIME = 1000 * 60 * 60 * 8; //8 hours

type BidsState = {
    bidRows?: BidRow[],
    uniqueLocations?: PhysicalLocation[],
    uniqueCommodities?: Commodity[],
    filteredLocation?: PhysicalLocation,
    filteredCommodity?: Commodity,
    errors: string[],
    pollingTimeLimit?: number,
    showSessionTimeout: boolean,
    isVisible: boolean
};

const BidsComponent: React.FC<BidsComponentProps> = props => {

    const context = useContext(BidsContext);
    const [state, setState] = useState<BidsState>({
        errors: [],
        showSessionTimeout: false,
        isVisible: false
    });

    const [selectedCommoditySettings, setSelectedCommoditySettings] = useState<ExternalPreferences[]>([]);

    function getLastSelectedUnit(commodity: Commodity, location: LocationBase): Unit {
        const selectedUnitId = selectedCommoditySettings.find(x => x.locationId === location.id && x.commodityId === commodity.id)?.selectedUnit.id;
        if (selectedUnitId == null || commodity.baseUnit.id === selectedUnitId) {
            return commodity.baseUnit;
        }
        if (commodity.localUnit.id === selectedUnitId) {
            return commodity.localUnit;
        }
    }

    function onUnitSelected(commodity: Commodity, unit: Unit, location: LocationBase = AllRegionsHomeLocation) {
        const settingsCopy = [...selectedCommoditySettings];
        const currentCommodity = settingsCopy.find(x => x.locationId === location.id && x.commodityId === commodity.id);
        if (currentCommodity == null) {
            settingsCopy.push(new ExternalPreferences(commodity.id, location.id, unit))
        }
        else {
            currentCommodity.selectedUnit = unit;
        }
        setSelectedCommoditySettings(settingsCopy);
    }

    useEffect(() => {
        resetTimeUntilExpiration();
        retrieveData();
    }, []);


    const cssUrl = `${process.env.REACT_APP_PROXY_API_BASE_URL}/settings/v1/files/styles/bid-component${context.preview ? ".preview" : ""}.css?key=${context.apiKey}`;

    const sortOrder: BidRowAccessor[] = [
        m => context.locations == null ? m.location.name : context.locations.indexOf(m.location.id), 
        m => context.commodities == null ? m.commodity.name : context.commodities.indexOf(m.commodity.id)
    ];

    async function retrieveData() {
        try {
            const response = await externalBidsService.listDeliveryPeriods(context.apiKey, context.commodities, context.locations);
            const dps = response.filter(dp => dp.bids.length > 0);
            const rows = dps.flatMap(dp => dp.bids
                .filter(b => (context.commodities == null || context.commodities.includes(dp.commodity.id) &&
                             (context.locations == null || context.locations.includes(b.location.id))) &&
                             (!context.hideRowsWithoutBasis || (b.basis != null && b.cash != null)))
                .map(b => ({
                    deliveryPeriodId: dp.id,
                    deliveryPeriodName: dp.name,
                    start: dp.start,
                    end: dp.end,
                    futuresPrice: dp.contract?.price,
                    futuresPriceUpdatedDate: dp.contract?.priceUpdated,
                    futuresChange: dp.contract?.netChange,
                    futuresMonth: dp.contract?.maturityMonth,
                    futuresYear: dp.contract?.maturityYear,
                    isSettled: dp.contract?.isSettled,
                    commodity: dp.commodity,
                    location: b.location,
                    cash: b.cash,
                    basis: b.basis,
                    lastUpdatedDate: dp.lastUpdatedDateUtc,
                }))
            );
            setState(m => ({...m,
                bidRows: _.orderBy(rows, context.byCommodity ? sortOrder : sortOrder.reverse()),
                uniqueLocations: _.orderBy(_.uniqBy(rows.map(m => m.location), m => m.id), m => m.name),
                uniqueCommodities: _.orderBy(_.uniqBy(rows.map(m => m.commodity), m => m.id), m => m.name)
            }))
        }
        catch (err) {
            handleError(err);
        }
    }

    function resetTimeUntilExpiration() {
        setState(m => ({...m, pollingTimeLimit: Date.now() + REFRESH_EXPIRATION_TIME}))
        
        setTimeout(() => {
            setState(m => ({...m, showSessionTimeout: true}))
        }, REFRESH_EXPIRATION_TIME);
    }

    useInterval(() => {
        if (Date.now() < state.pollingTimeLimit) retrieveData();
    }, BID_REFRESH_INTERVAL);

    function handleRefresh() {        
        setState(m => ({...m, showSessionTimeout: false}))
        resetTimeUntilExpiration();
        retrieveData();
    }

    function getUpdatedDate(bids: BidRow[]) {
        const filtered = bids.filter(x => x.commodity.isCashOnly === true ||  (x.commodity.isCashOnly === false && x.futuresPriceUpdatedDate != null));
        return moment.max(filtered.map(x => moment(x.commodity.isCashOnly ? x.lastUpdatedDate: x.futuresPriceUpdatedDate))).toDate();
    }

    function handleError(err: any) {
        let errorToShow = "Unable to load component";
        if (err.isAxiosError) {
            const axiosError = err as AxiosError;

            if (axiosError.response == null)  {
                errorToShow = "Unable to contact server";
            }
            else {
                switch (axiosError.response?.status) {
                    case 404:
                        errorToShow = "Unable to contact server (error code 404)";
                        break;
                    case 401:
                        errorToShow = "Unable to authenticate with server. Please verify your authentication key is set correctly (error code 401)";
                        break;
                }
            }
        }
        setState(m => ({...m, errors: [errorToShow]}))
        loggingService.error("Unable to retrieve delivery periods", err);
    }

    if (state.errors.length > 0) {
        return <ComponentWrapper><ErrorList errors={state.errors} /></ComponentWrapper>;
    }

    if (!state.bidRows) return <Loading />;

    let groupingFuncs: BidRowAccessor[] = [m => m.location.name, m => m.commodity.name];

    if (context.byCommodity) groupingFuncs = groupingFuncs.reverse();

    const filteredBids = state.bidRows.filter(m => 
        (state.filteredLocation == null || m.location.id === state.filteredLocation.id) &&
        (state.filteredCommodity == null || m.commodity.id === state.filteredCommodity.id));

    const groupedTopLevel = groupBids(filteredBids, groupingFuncs[0]);

    return <ComponentWrapper>
        <link rel="stylesheet" href={cssUrl}
            onLoad={() => setState(m => ({...m, isVisible: true})) }
            onError={() => setState(m => ({...m, isVisible: true})) } />

        {state.isVisible && <>
            {state.showSessionTimeout && <SessionTimeout onRefresh={handleRefresh} />}

            {!state.showSessionTimeout && <>
                <Form.Group className="form-inline">
                    {state.uniqueLocations?.length > 1 && 
                            <Form.Control as="select" className="location-picker mr-2"
                                value={state.filteredLocation?.id ?? ""}
                                onChange={ev => setState(m => ({...m, filteredLocation: state.uniqueLocations.find(m => m.id === ev.target.value)}))}>
                                <option value="">All Locations</option>
                                {state.uniqueLocations.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </Form.Control>
                    }
                    {state.uniqueCommodities?.length > 1 && 
                            <Form.Control as="select" className="commodity-picker"
                                value={state.filteredCommodity?.id ?? ""}
                                onChange={ev => setState(m => ({...m, filteredCommodity: state.uniqueCommodities.find(m => m.id === Number.parseInt(ev.target.value))}))}>
                                <option value="">All Commodities</option>
                                {state.uniqueCommodities.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </Form.Control>
                    }

                    <CSVLink className="download-button"
                        headers={csvHeaders}
                        data={getCsvData(filteredBids)}
                        filename={`CashBids ${formatDate(new Date(),"YYYY-MM-DD")}.csv`}>
                                <i className="material-icons">download</i>
                                <span className="info-text">Download</span>
                    </CSVLink>
                </Form.Group>
                {groupedTopLevel.length === 0 && <div>No matching bids found</div>}
                {groupedTopLevel.length > 0 && <LastUpdated lastUpdated={getUpdatedDate(groupedTopLevel.flatMap(x => x.bids))} />}
                {groupedTopLevel.map(m => {
                    const groupedCommodity = context.byCommodity && m.bids ? m.bids[0].commodity : null;
                    const groupedLocation = !context.byCommodity && m.bids ? m.bids[0].location : null;
                    const selectedLocation: LocationBase = groupedLocation ? { id: groupedLocation.id, name: groupedLocation.name } : AllRegionsHomeLocation;
                    return <GroupedBids key={m.title}
                        title={m.title}
                        commodity={groupedCommodity}
                        groups={groupBids(m.bids, groupingFuncs[1])}
                        getLastSelectedUnit={x => getLastSelectedUnit(x, selectedLocation)}
                        onUnitSelected={onUnitSelected}
                        location={selectedLocation}
                    />
                })}
            </>}

            <div className="disclaimer">
                Data provided by <a href="https://stonex.com" target="_blank">StoneX</a>. All prices delayed 10 minutes.
                <a href="https://www.cmegroup.com/" target="_blank"><img src={CmeLogo} /></a>
            </div>
        </>}
    </ComponentWrapper>;
}

function groupBids(bids: BidRow[], by: BidRowAccessor): {title: string, bids: BidRow[]}[] {
    return _.map(
        _.groupBy(bids, by),
        (g, key) => ({title: key, bids: g})
    );
}

const ComponentWrapper: React.FC<any> = props => <div className="stonex-bids-component">{props.children}</div>;


interface GroupedBidsProps {
    title: string;
    groups: { title: string, bids: BidRow[] }[];
    commodity: Commodity;
    onUnitSelected(commodity: Commodity, unit: Unit, location?: LocationBase): void;
    getLastSelectedUnit(commodity: Commodity): Unit;
    location: LocationBase;
}

const GroupedBids: React.FC<GroupedBidsProps> = props => {
    const context = useContext(BidsContext);
    const commodity = props.commodity;

    return <div className="bid-group">
        <div className="ml-0 bid-group-name row">
            {props.title}
            {context.byCommodity && commodity.baseUnit.id !== commodity.localUnit.id &&
                <>
                    <UnitPicker className="ml-3 mr-3 unit-picker"
                        options={[commodity.baseUnit, commodity.localUnit]}
                        selectedUnit={props.getLastSelectedUnit(commodity)}
                        onUnitSelected={u => { props.onUnitSelected(commodity, u) }} />
                    <div className="currency-code">
                        All prices in {commodity.localCurrency.code}
                    </div>
                </>
            }
        </div>

        {props.groups.map(m =>
            <BidsTable
                key={m.title}
                title={m.title}
                bids={m.bids}
                commodity={!context.byCommodity && m.bids ? m.bids[0].commodity : commodity}
                onUnitSelected={props.onUnitSelected}
                getLastSelectedUnit={props.getLastSelectedUnit}
                location={props.location}
            />)}
    </div>
}

interface BidsTableProps {
    title: string;
    bids: BidRow[];
    commodity: Commodity;
    onUnitSelected(commodity: Commodity, unit: Unit, location?: LocationBase): void;
    getLastSelectedUnit(commodity: Commodity, location: LocationBase): Unit;
    location: LocationBase;
}

const BidsTable: React.FC<BidsTableProps> = props => {
    const commodity = props.commodity;
    const [columns, setColumns] = useState<BidColumn[]>(null);
    const context = useContext(BidsContext);
    const selectedUnit = props.getLastSelectedUnit(commodity, props.location);

    useEffect(() => {
        setColumns((context.columns ?? defaultColumns)
            .map(m => bidColumns[m])
            .filter(m => m != null));
    }, [context.columns])

    if (columns == null) return null;

    return <>
        <div className="bid-table">
            <div className="ml-0 bid-table-name row">
                {props.title}
                {!context.byCommodity && commodity.baseUnit.id !== commodity.localUnit.id &&
                    <>
                        <UnitPicker className="ml-3 mr-3 unit-picker"
                            options={[commodity.baseUnit, commodity.localUnit]}
                            selectedUnit={selectedUnit}
                            onUnitSelected={u => { props.onUnitSelected(commodity, u, props.location) }} />
                        <div className="currency-code">
                            All prices in {commodity.localCurrency.code}
                        </div>
                    </>
                }
            </div>
            <div className="bid-table-header">
                {columns.map(m => <div key={m.key} className={m.key}>{m.name}</div>)}
            </div>
            {props.bids.map(m => <div key={`${m.deliveryPeriodId}-${m.location.id}`} className="bid-table-row">
                {columns.map(c => <div key={c.key} className={c.key}>{c.renderer(m, selectedUnit)}</div>)}
            </div>)}
        </div>
    </>;
}

interface SessionTimeoutProps {
    onRefresh(): void;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = props => {

    return <div>
        <h2>Your session has timed out</h2>

        <p>To continue viewing bids, <button className="link-button" onClick={() => props.onRefresh()}>click here</button></p>
    </div>
}

const csvHeaders: Headers = [
    { label: "Commodity", key: "commodity.name" },
    { label: "Location", key: "location.name" },
    { label: "Delivery Period Name", key: "deliveryPeriodName" },
    { label: "Start", key: "startString" },
    { label: "End", key: "endString" },
    { label: "Futures Month", key: "futuresMonth" },
    { label: "Futures Year", key: "futuresYear" },
    { label: "Futures", key: "futuresPrice" },
    { label: "Basis", key: "basis" },
    { label: "Cash", key: "cash" },
    { label: "Futures Change", key: "futuresChange" }
];

interface CsvExportRow extends BidRow {
    startString: string;
    endString: string;
}

function getCsvData(data: BidRow[]): CsvExportRow[] {
    return data.map(m => ({
        ...m,
        startString: formatUTCDate(m.start),
        endString: formatUTCDate(m.end),
    }));
}


class ExternalPreferences {
    commodityId: number = null;
    locationId: string = null;
    selectedUnit: Unit = null;

    constructor(commodityId: number, locationId: string, unit: Unit) {
        this.commodityId = commodityId;
        this.locationId = locationId;
        this.selectedUnit = unit;
    }
}

export default BidsComponent;